import { createBrowserRouter, Navigate } from "react-router-dom";

import {
  MainLayout,
  PrivateLayout,
  PublicLayout,
  RoleBasedLayout,
} from "./layouts";
import {
  ClientsPage,
  DishesPage,
  ForgotPasswordPage,
  LoginPage,
  MenuPage,
  MessagesPage,
  OrdersPage,
  SettingsPage,
  StatisticsPage,
  StoragePage,
} from "./pages";
import {
  MealsComponent,
  MenuCategoriesComponent,
  PacksComponent,
  PeriodsComponent,
  PriceComponent,
  ProductGroupsComponent,
  ProductsComponent,
  PromoCodesComponent,
  SettingsComponent,
  PaymentSettingsComponent,
  ClientSettingsComponent,
} from "./components/settingsSection";
import { MenuComponent, SampleMenuComponent, WeeklyMenuComponent } from "./components/menuSection";
import { BuilderPage } from "./pages";
import { AvailableDishesComponent } from "./components/menuSection/AvailableDishesComponent/AvailableDishesComponent";
import {
  DeliveriesComponent as OrdersDeliveriesComponent,
  OrdersDishesComponent,
  OrdersOrdersComponent,
  OrdersProductsComponent,
  OrdersFilesComponent,
} from "./components/ordersSection";
import {
  DeliveriesSComponent,
  OrdersSComponent,
} from "./components/statistickSection";
import { ReplacementsComponent } from "./components/statistickSection/ReplacementsComponent/ReplacementsComponent";
import { RequiredAuth } from "./hok";
import DishesComponent from "./components/dishesSection/DishesComponent/DishesComponent";
import { SemiFinishedComponent } from "./components/semiFinishedSection/SemiFinishedComponent/SemiFinishedComponent";
import LoggedInDevices from "./components/LoggedInDevices/loggedin-devices";
import BulkUpdateDishesPricesComponent from "./components/dishesSection/BulkUpdateDishesPricesComponent/BulkUpdateDishesPricesComponent";
import UpdateDishComponent from "./components/dishesSection/UpdateDishComponent/UpdateDishComponent";
import ProductBrandsComponent from "./components/settingsSection/ProductBrandsComponent/ProductBrandsComponent";
import DietModeAndTypes from "./components/settingsSection/DietModeAndTypes/DietModeAndTypes";
import { PackCategoriesComponent } from "./components/settingsSection/PackCategoriesComponent/PackCategoriesComponent";
import { ROLES } from "./constants";
import QuizResponse from "./components/messagesSection/QuizResponse/QuizResponse";
import OrderChangelog from "./components/messagesSection/OrderChangelog/OrderChangelog";
import { DishesStatisticsComponent } from "./components/statistickSection/DishesStatisticsComponent/DishesStatisticsComponent";

const router = createBrowserRouter([
  {
    path: "/",
    element: <MainLayout />,
    children: [
      { index: true, element: <Navigate to={"/login"} /> },
      {
        element: <PublicLayout />,
        children: [
          { path: "/login", element: <LoginPage /> },
          { path: "/forgot-password", element: <ForgotPasswordPage /> },
        ],
      },
      {
        element: (
          <RequiredAuth>
            <RoleBasedLayout allowedRoles={[ROLES.ADMIN, ROLES.NUTRITIONIST]} />
          </RequiredAuth>
        ),
        children: [
          {
            path: "/settings",
            element: <SettingsPage />,
            children: [
              { index: true, element: <Navigate to={"menu-categories"} /> },
              { path: "menu-categories", element: <MenuCategoriesComponent /> },
              { path: "meals", element: <MealsComponent /> },
              { path: "products", element: <ProductsComponent /> },
              { path: "product-brands", element: <ProductBrandsComponent /> },
              { path: "product-groups", element: <ProductGroupsComponent /> },
            ],
          },
          {
            path: "/dishes",
            element: <DishesPage />,
            children: [
              { index: true, element: <Navigate to={"dishes"} /> },
              { path: "dishes", element: <DishesComponent /> },
              {
                path: "bulk-update-dishes-prices",
                element: <BulkUpdateDishesPricesComponent />,
              },
              { path: "semi-finished", element: <SemiFinishedComponent /> },
              { path: "update-dish/:id", element: <UpdateDishComponent /> },
            ],
          },
          {
            path: "/menu",
            element: <MenuPage />,
            children: [
              { index: true, element: <Navigate to={"menu"} /> },
              { path: "menu", element: <MenuComponent /> },
              {
                path: "available-dishes",
                element: <AvailableDishesComponent />,
              },
              { path: "sample-menu", element: <SampleMenuComponent /> },
              { path: "7-day-menu", element: <WeeklyMenuComponent /> },
            ],
          },
        ],
      },
      {
        element: (
          <RequiredAuth>
            <RoleBasedLayout allowedRoles={[ROLES.ADMIN]} />
          </RequiredAuth>
        ),
        children: [
          {
            path: "/settings",
            element: <SettingsPage />,
            children: [
              { index: true, element: <Navigate to={"menu-categories"} /> },
              { path: "menu-categories", element: <MenuCategoriesComponent /> },
              { path: "meals", element: <MealsComponent /> },
              { path: "packs", element: <PacksComponent /> },
              { path: "periods", element: <PeriodsComponent /> },
              { path: "price", element: <PriceComponent /> },
              { path: "promo-codes", element: <PromoCodesComponent /> },
              { path: "products", element: <ProductsComponent /> },
              { path: "product-brands", element: <ProductBrandsComponent /> },
              { path: "product-groups", element: <ProductGroupsComponent /> },
              { path: "settings", element: <SettingsComponent /> },
              { path: "payment-settings", element: <PaymentSettingsComponent /> },
              { path: "client-settings", element: <ClientSettingsComponent /> },
              { path: "devices", element: <LoggedInDevices /> },
              { path: "diet-mode-and-types", element: <DietModeAndTypes /> },
              { path: "pack-categories", element: <PackCategoriesComponent /> },
            ],
          },
          {
            path: "/orders",
            element: <OrdersPage />,
            children: [
              { index: true, element: <Navigate to={"orders"} /> },
              { path: "orders", element: <OrdersOrdersComponent /> },
              { path: "dishes", element: <OrdersDishesComponent /> },
              { path: "products", element: <OrdersProductsComponent /> },
              { path: "deliveries", element: <OrdersDeliveriesComponent /> },
              { path: "files", element: <OrdersFilesComponent /> },
            ],
          },
          { path: "/clients", element: <ClientsPage />, children: [] },
          {
            path: "/statistics",
            element: <StatisticsPage />,
            children: [
              { index: true, element: <Navigate to={"deliveries"} /> },
              { path: "deliveries", element: <DeliveriesSComponent /> },
              { path: "orders", element: <OrdersSComponent /> },
              { path: "replacements", element: <ReplacementsComponent /> },
              { path: "dishes", element: <DishesStatisticsComponent /> },
            ],
          },
          { path: "/messages", element: <MessagesPage />, children: [
            { index: true, element: <Navigate to={"wellness-quiz"} /> },
            { path: "wellness-quiz", element: <QuizResponse /> },
            { path: "changelog", element: <OrderChangelog /> },
          ] },
          { path: "/storage", element: <StoragePage /> },
          { path: "/builder", element: <BuilderPage /> },
        ],
      },
    ],
  },
]);

export { router };
